import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Video, Presentation, Loader2 } from 'lucide-react';
import { PitchData } from '../../types/company';

/**
 * Props du composant PitchSection
 * 
 * @property label - Label affiché au-dessus de la section
 * @property value - Données pitch actuelles (depuis formData.pitch)
 * @property onSave - Callback appelé lors de la sauvegarde (peut être sync ou async)
 * @property disabled - Si true, le composant est en lecture seule
 */
interface PitchSectionProps {
  label: string;
  value: PitchData;
  onSave: (pitch: PitchData) => void | Promise<void>;
  disabled?: boolean;
}

export const PitchSection: React.FC<PitchSectionProps> = ({
  label,
  value,
  onSave,
  disabled = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // ✨ Initialiser avec des valeurs par défaut pour éviter undefined
  const getDefaultPitch = useCallback((): PitchData => ({
    elevator_pitch: '',
    value_proposition: '',
    target_market: '',
    competitive_advantage: '',
    pitch_deck_url: '',
    demo_video_url: '',
    ...value
  }), [value]);
  
  const [tempPitch, setTempPitch] = useState<PitchData>(getDefaultPitch);

  // ✨ SYNC: Mettre à jour tempPitch quand value change depuis le parent
  // Seulement si on n'est pas en mode édition (pour ne pas écraser les modifications en cours)
  useEffect(() => {
    if (!isEditing) {
      setTempPitch(getDefaultPitch());
    }
  }, [value, isEditing, getDefaultPitch]);

  const startEditing = () => {
    if (disabled) return;
    setIsEditing(true);
    setTempPitch(getDefaultPitch());
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setTempPitch(getDefaultPitch());
  };

  const saveChanges = async () => {
    try {
      setIsSaving(true);
      // onSave peut être sync ou async, on gère les deux cas
      await Promise.resolve(onSave(tempPitch));
      setIsEditing(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du pitch:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field: keyof PitchData, newValue: string) => {
    setTempPitch(prev => ({
      ...prev,
      [field]: newValue
    }));
  };

  const hasContent = () => {
    return Object.values(value).some(val => val && val.trim() !== '');
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>

      {!isEditing ? (
        // Mode affichage
        <div
          onClick={startEditing}
          className={`
            min-h-[2.5rem] p-4 border border-gray-300 rounded-lg
            ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}
            transition-colors duration-200
          `}
        >
          {hasContent() ? (
            <div className="space-y-3">
              {value.elevator_pitch && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Elevator Pitch</h4>
                  <p className="text-sm text-gray-600">{value.elevator_pitch}</p>
                </div>
              )}
              
              {value.value_proposition && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Proposition de valeur</h4>
                  <p className="text-sm text-gray-600">{value.value_proposition}</p>
                </div>
              )}
              
              {value.target_market && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Marché cible</h4>
                  <p className="text-sm text-gray-600">{value.target_market}</p>
                </div>
              )}
              
              {value.competitive_advantage && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Avantage concurrentiel</h4>
                  <p className="text-sm text-gray-600">{value.competitive_advantage}</p>
                </div>
              )}
              
              {(value.pitch_deck_url || value.demo_video_url) && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
                  {value.pitch_deck_url && (
                    <a
                      href={value.pitch_deck_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-1 text-[#197ca8] hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Presentation className="w-4 h-4" />
                      <span className="text-sm">Pitch Deck</span>
                    </a>
                  )}
                  {value.demo_video_url && (
                    <a
                      href={value.demo_video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-1 text-[#197ca8] hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Video className="w-4 h-4" />
                      <span className="text-sm">Vidéo de démonstration</span>
                    </a>
                  )}
                </div>
              )}
            </div>
          ) : (
            <span className="text-gray-400 text-sm flex items-center">
              <FileText className="w-4 h-4 mr-2" />
              Cliquez pour ajouter votre pitch et présentation
            </span>
          )}
        </div>
      ) : (
        // Mode édition
        <div className="space-y-4 border border-gray-300 rounded-lg p-4">
          {/* Elevator Pitch */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Elevator Pitch
              <span className="text-xs text-gray-500 ml-2">
                ({tempPitch.elevator_pitch?.length || 0}/300 caractères)
              </span>
            </label>
            <textarea
              value={tempPitch.elevator_pitch}
              onChange={(e) => {
                if (e.target.value.length <= 300) {
                  updateField('elevator_pitch', e.target.value);
                }
              }}
              placeholder="Décrivez votre entreprise en quelques phrases percutantes..."
              rows={3}
              maxLength={300}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] resize-none"
            />
          </div>

          {/* Proposition de valeur */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Proposition de valeur unique
            </label>
            <textarea
              value={tempPitch.value_proposition}
              onChange={(e) => updateField('value_proposition', e.target.value)}
              placeholder="Qu'est-ce qui vous rend unique ? Quel problème résolvez-vous ?"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] resize-none"
            />
          </div>

          {/* Marché cible */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Marché cible
            </label>
            <textarea
              value={tempPitch.target_market}
              onChange={(e) => updateField('target_market', e.target.value)}
              placeholder="Décrivez votre marché cible et vos clients idéaux..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] resize-none"
            />
          </div>

          {/* Avantage concurrentiel */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Avantage concurrentiel
            </label>
            <textarea
              value={tempPitch.competitive_advantage}
              onChange={(e) => updateField('competitive_advantage', e.target.value)}
              placeholder="Qu'est-ce qui vous différencie de vos concurrents ?"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] resize-none"
            />
          </div>

          {/* Liens vers documents */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Pitch Deck (URL)
              </label>
              <input
                type="url"
                value={tempPitch.pitch_deck_url || ''}
                onChange={(e) => updateField('pitch_deck_url', e.target.value)}
                placeholder="https://exemple.com/pitch-deck.pdf"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8]"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Vidéo de démonstration (URL)
              </label>
              <input
                type="url"
                value={tempPitch.demo_video_url || ''}
                onChange={(e) => updateField('demo_video_url', e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8]"
              />
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex justify-end space-x-2 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={cancelEditing}
              disabled={isSaving}
              className="px-3 py-2 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={saveChanges}
              disabled={isSaving}
              className="px-3 py-2 text-sm bg-[#197ca8] text-white rounded hover:bg-[#156a92] disabled:opacity-50 flex items-center"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Sauvegarde...
                </>
              ) : (
                'Sauvegarder'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};